import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { safeLocalGet, safeLocalRemove, safeLocalSet } from "src/utils/storage";
import { API_URL } from "src/utils/apiUrl";
import { captureError } from "src/utils/errorReporting";
import { switchActiveCompany, persistDefaultCompany } from "src/utils/auth/switchCompanyApi";
import { decodeJwtPayload, isJwtExpired } from "src/utils/auth/jwtClaims";
import { startImpersonation, endImpersonationApi, extendImpersonationApi } from "src/utils/auth/impersonateApi";
import { revokeSession } from "src/utils/auth/sessionApi";
import { planLogoutTeardown } from "src/utils/auth/logoutTeardown";
import { useLang, useT } from "src/i18n";
const STORAGE_KEY = "betonijerry.auth";
// Full admin auth object stashed here while impersonating, so "return to admin"
// restores it without a re-login. Separate key so the 10-min imp token's expiry
// can't take the admin session down with it.
const IMPERSONATOR_KEY = "betonijerry.impersonator";

// Hardcoded mirror of @ibetoni/constants BETONIJERRY_OWNER_ASIAKAS_ID.
// betonijerry is not part of the npm workspace so we cannot import the shared
// constant. Keep this in sync with ibetoni_packages/constants/src/betonijerry.js.
const BETONIJERRY_OWNER_ASIAKAS_ID = 1349;

const AuthContext = createContext(null);

async function postJson(path, body) {
    const res = await fetch(`${API_URL}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        const err = new Error(data.message || `HTTP ${res.status}`);
        err.status = res.status;
        err.code = data.code; // stable machine code for errorCopy (fb#2184)
        throw err;
    }
    return data;
}

export function AuthProvider({ children }) {
    const { lang } = useLang();
    const t = useT();
    const [auth, setAuth] = useState(() => {
        const stored = safeLocalGet(STORAGE_KEY);
        if (stored && isJwtExpired(stored.token)) {
            // An impersonation session whose 10-min token expired: fall back to the
            // stashed admin login instead of logging the operator out entirely.
            const stash = safeLocalGet(IMPERSONATOR_KEY);
            safeLocalRemove(IMPERSONATOR_KEY);
            if (stash && !isJwtExpired(stash.token)) {
                safeLocalSet(STORAGE_KEY, stash);
                return stash;
            }
            safeLocalRemove(STORAGE_KEY);
            return null;
        }
        return stored;
    });

    useEffect(() => {
        if (auth) safeLocalSet(STORAGE_KEY, auth);
        else safeLocalRemove(STORAGE_KEY);
    }, [auth]);

    // `source: "betonijerry"` mirrors the OAuth calls so the backend's
    // maybeLazyAttachBetonijerryUmbrella can heal returning users whose
    // Person row predates resolveOAuthOwnerAsiakasId (2026-05-13).
    const login = useCallback(async (email, password) => {
        const data = await postJson("/api/auth/login/loginWithEmailAndPassword", {
            email,
            password,
            source: "betonijerry",
        });
        setAuth(data);
        return data;
    }, []);

    // `source: "betonijerry"` tells the backend's PersonAuthController to attach
    // first-time OAuth users to the BetoniJerry umbrella tenant (ownerAsiakasId=1349)
    // instead of creating a tenant-less Person row. Returning users ignore the field.
    const loginWithGoogle = useCallback(async (idToken) => {
        const data = await postJson("/api/auth/login/loginWithGoogleToken", {
            token: idToken,
            source: "betonijerry",
        });
        setAuth(data);
        return data;
    }, []);

    const loginWithMicrosoft = useCallback(async (idToken) => {
        const data = await postJson("/api/auth/login/loginWithMicrosoftToken", {
            token: idToken,
            source: "betonijerry",
        });
        setAuth(data);
        return data;
    }, []);

    const loginWithApple = useCallback(async ({ idToken, firstName, lastName }) => {
        const data = await postJson("/api/auth/login/loginWithAppleToken", {
            token: idToken,
            source: "betonijerry",
            firstName,
            lastName,
        });
        setAuth(data);
        return data;
    }, []);

    const loginWithLinkedIn = useCallback(async ({ code, codeVerifier, redirectUri }) => {
        const data = await postJson("/api/auth/login/loginWithLinkedInToken", {
            code,
            codeVerifier,
            redirectUri,
            source: "betonijerry",
        });
        setAuth(data);
        return data;
    }, []);

    /**
     * Probe whether an email is registered (email-first auth step).
     * Returns { isAvailable, hasPassword }.
     */
    const checkEmail = useCallback(async (email) => {
        const data = await postJson("/api/auth/registration/check-email", { email });
        return {
            isAvailable: !!data.isAvailable,
            hasPassword: !!data.hasPassword,
        };
    }, []);

    /** Step 1 of register — emails a verification code. Returns sessionId. */
    const startRegistration = useCallback(async (email) => {
        // source: "betonijerry" brands this — the FIRST email a new signup
        // receives — since no person row exists yet for the backend to derive
        // brand from otherwise. language picks fi/en copy.
        const data = await postJson("/api/auth/registration/send-verification-code", {
            email,
            source: "betonijerry",
            language: lang,
        });
        return data.sessionId;
    }, [lang]);

    /** Step 2 of register — verifies the email code. Returns true on success. */
    const verifyRegistrationCode = useCallback(async ({ email, code, sessionId }) => {
        await postJson("/api/auth/registration/verify-code", { email, code, sessionId });
        return true;
    }, []);

    /**
     * Step 1 of passwordless email-code login — emails a sign-in code.
     * Returns the sessionId to thread into loginWithEmailCode.
     */
    const sendLoginCode = useCallback(async (email) => {
        // source is a fallback signal only — the backend prefers the existing
        // person's own ownerAsiakasId when resolving brand for this send.
        const data = await postJson("/api/auth/login/send-email-code", {
            email,
            source: "betonijerry",
            language: lang,
        });
        return data.sessionId;
    }, [lang]);

    /**
     * Step 2 of passwordless email-code login — verifies the code, persists
     * the returned auth payload (same shape as `login`), and returns it.
     */
    const loginWithEmailCode = useCallback(async ({ email, code, sessionId }) => {
        const data = await postJson("/api/auth/login/loginWithEmailCode", {
            email,
            code,
            sessionId,
            source: "betonijerry",
        });
        setAuth(data);
        return data;
    }, []);

    /** Password reset — step 1: email a 4-digit reset code. Fire-and-forget. */
    const requestPasswordResetCode = useCallback(async (email) => {
        // source is a fallback signal only — the backend prefers the existing
        // person's own ownerAsiakasId when resolving brand for this send.
        await postJson("/api/auth/password-reset/request-code", {
            email,
            source: "betonijerry",
            language: lang,
        });
        return true;
    }, [lang]);

    /**
     * Password reset — step 2: verify the code + set the new password. The
     * backend doesn't return an auth payload; the caller is responsible for
     * following up with `login(email, newPassword)` to land the user
     * authenticated. The split is intentional so the UI can distinguish a
     * "reset failed" error from a "reset OK but login failed" error.
     */
    const verifyPasswordResetCode = useCallback(async ({ email, code, newPassword }) => {
        await postJson("/api/auth/password-reset/verify-code", { email, code, newPassword });
        return true;
    }, []);

    /**
     * Step 3 of register — creates the account (and the customer asiakas + link
     * via the betonijerry customerAsiakas block). Persists the returned auth.
     */
    const completeRegistration = useCallback(async ({
        email, sessionId, password,
        etunimi, sukunimi, puhelin,
        isYritys, yritysNimi, ytunnus,
    }) => {
        const customerAsiakas = isYritys
            ? { asiakasNimi: yritysNimi, asiakasTypeId: 1, ytunnus }
            : { asiakasNimi: `${etunimi} ${sukunimi}`.trim(), asiakasTypeId: 2, ytunnus: null };

        const data = await postJson("/api/auth/registration/create-account", {
            email,
            sessionId,
            password,
            personFirstName: etunimi,
            personLastName: sukunimi,
            personPhone: puhelin,
            ownerAsiakasId: BETONIJERRY_OWNER_ASIAKAS_ID,
            customerAsiakas,
            language: lang,
        });
        setAuth(data);
        return data;
    }, [lang]);

    // Logout is also the app-wide 401 handler (~10 call sites), so it must stay
    // instant and keep a STABLE identity (several effects list it as a dep) — we
    // read storage at call time instead of closing over `auth`, keeping deps [].
    // All server calls are fire-and-forget: a real logout must never block on the
    // network, and revocation/audit are best-effort.
    const logout = useCallback(() => {
        const active = safeLocalGet(STORAGE_KEY);
        const stash = safeLocalGet(IMPERSONATOR_KEY);
        const activeToken = active?.token ?? null;
        const { endImpersonation, tokensToRevoke } = planLogoutTeardown({
            activeToken,
            activeIsImpersonation: decodeJwtPayload(activeToken)?.imp != null,
            stashToken: stash?.token ?? null,
        });

        if (endImpersonation) {
            endImpersonationApi({ token: activeToken, endReason: "logout" }).catch((err) =>
                captureError(err, { tags: { feature: "auth", operation: "impersonate-end-on-logout" } }));
        }
        for (const t of tokensToRevoke) {
            revokeSession(t).catch((err) =>
                captureError(err, { tags: { feature: "auth", operation: "logout-revoke" } }));
        }

        safeLocalRemove(IMPERSONATOR_KEY);
        setAuth(null);
    }, []);

    /**
     * Merge updated person fields into the cached auth.person[0] without a
     * server refetch. Used after PATCH /api/me/person so useCurrentPerson
     * reflects the new name/phone/email immediately. `fields` is the partial
     * person row the PATCH response returned.
     */
    const applyPersonUpdate = useCallback((fields) => {
        if (!fields) return;
        setAuth((prev) => {
            if (!prev?.person?.[0]) return prev;
            const [head, ...rest] = prev.person;
            return { ...prev, person: [{ ...head, ...fields }, ...rest] };
        });
    }, []);

    /**
     * Switch the active company (operators who belong to more than one). Re-mints
     * the JWT via the backend — membership is re-verified server-side, so a caller
     * can only switch to a company they actually belong to — then swaps the stored
     * token, which re-scopes every ownerAsiakasId-keyed view (the TARJOUSPYYNNÖT
     * inbox, offers, Hallinta) on the next fetch. Also persists the choice as the
     * user's default company (typeId 15) so it survives the next login.
     *
     * The default-persist is fire-and-forget: losing it only means the next login
     * falls back to the previous default — the in-session switch already took hold.
     */
    const switchCompany = useCallback(async (newAsiakasId) => {
        const data = await switchActiveCompany({ token: auth?.token, newAsiakasId });
        if (!data?.token) throw new Error(t("common.auth.switchCompanyFailed"));
        setAuth((prev) => (prev ? { ...prev, token: data.token } : prev));

        const personId = auth?.person?.[0]?.personId;
        if (personId) {
            persistDefaultCompany({ token: data.token, personId, asiakasId: Number(newAsiakasId) })
                .catch((err) => captureError(err, { tags: { entity: "person", operation: "persist-default-company" } }));
        }
        return data;
    }, [auth, t]);

    // Decode the active token once; derive the active tenant + impersonation
    // signals from it. ownerAsiakasId is authoritative from the JWT (can differ
    // from person[0].ownerAsiakasId), so a switch/impersonate updates it as the
    // token swaps.
    const claims = useMemo(() => decodeJwtPayload(auth?.token), [auth]);
    const ownerAsiakasId = claims?.ownerAsiakasId ?? null;
    const canImpersonate = !!(claims?.globalRoles?.isSystemAdmin || claims?.globalRoles?.isRoleManager);
    const isImpersonating = claims?.imp != null;

    // Token to use for the staff-only impersonation-favorites endpoints. While
    // impersonating, the active token is the target's 10-min imp token (NOT an
    // admin — it would 403 those routes), so reach for the stashed admin session;
    // otherwise the active token already is the admin's own.
    const adminToken = useMemo(
        () => (isImpersonating ? safeLocalGet(IMPERSONATOR_KEY)?.token ?? null : auth?.token ?? null),
        [auth, isImpersonating]
    );

    /**
     * Start impersonating a target person (betoni.online staff → Jerry customer).
     * Stashes the current admin auth, then swaps the session to the target while
     * keeping the 10-min imp token as the session token.
     */
    const impersonate = useCallback(async ({ personId, email } = {}) => {
        const adminAuth = auth;
        const target = await startImpersonation({ token: adminAuth?.token, personId, email });
        if (!target?.token) throw new Error(t("common.auth.impersonateFailed"));
        safeLocalSet(IMPERSONATOR_KEY, adminAuth);
        setAuth(target);
        return target;
    }, [auth, t]);

    /** Return to the stashed admin session and write the impersonation_end row. */
    const returnToAdmin = useCallback(async (endReason = "manual") => {
        const stash = safeLocalGet(IMPERSONATOR_KEY);
        try {
            await endImpersonationApi({ token: auth?.token, endReason });
        } catch (err) {
            // The imp token lives 10 minutes and is never refreshed, so ending a
            // session that outlived it 401s — that is the expected path, not a
            // failure. The teardown below runs either way, so the admin still
            // gets their own session back.
            if (err?.status !== 401) {
                captureError(err, { tags: { feature: "auth", operation: "impersonate-end" } });
            }
        }
        safeLocalRemove(IMPERSONATOR_KEY);
        setAuth(stash || null);
        return true;
    }, [auth]);

    /** Renew the active impersonation session by 10 minutes (swaps the imp token). */
    const extendImpersonation = useCallback(async () => {
        const data = await extendImpersonationApi({ token: auth?.token });
        if (!data?.token) throw new Error(t("common.auth.extendSessionFailed"));
        setAuth((prev) => (prev ? { ...prev, token: data.token } : prev));
        return data;
    }, [auth, t]);

    const value = useMemo(() => ({
        user: auth,
        token: auth?.token || null,
        isAuthenticated: !!auth?.token,
        ownerAsiakasId,
        canImpersonate,
        isImpersonating,
        adminToken,
        impersonate,
        returnToAdmin,
        extendImpersonation,
        switchCompany,
        login,
        loginWithGoogle,
        loginWithMicrosoft,
        loginWithApple,
        loginWithLinkedIn,
        checkEmail,
        sendLoginCode,
        loginWithEmailCode,
        startRegistration,
        verifyRegistrationCode,
        completeRegistration,
        requestPasswordResetCode,
        verifyPasswordResetCode,
        logout,
        applyPersonUpdate,
    }), [
        auth, ownerAsiakasId, canImpersonate, isImpersonating, adminToken, impersonate, returnToAdmin, extendImpersonation, switchCompany,
        login, loginWithGoogle, loginWithMicrosoft, loginWithApple, loginWithLinkedIn,
        checkEmail, sendLoginCode, loginWithEmailCode,
        startRegistration, verifyRegistrationCode, completeRegistration,
        requestPasswordResetCode, verifyPasswordResetCode, logout, applyPersonUpdate,
    ]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const v = useContext(AuthContext);
    if (!v) throw new Error("useAuth must be used inside <AuthProvider>");
    return v;
}
